export type IdigestChatTheme = 'light' | 'dark';

export interface IdigestChatThemeTokens {
  bg: string;
  fg: string;
  muted: string;
  border: string;
  surface: string;
  surfaceHover: string;
  buttonBorder: string;
  buttonShadow: string;
  searchBorder: string;
  searchIcon: string;
  bubbleLeft: string;
  bubbleRight: string;
  bubbleBorder: string;
  quoteBg: string;
  quoteHover: string;
  inputBg: string;
  inputBorder: string;
  voiceIdle: string;
  voiceActive: string;
  dialogBg: string;
  dialogFg: string;
  dialogButtonBg: string;
  dialogButtonFg: string;
  uploadBarBg: string;
  uploadBarFg: string;
  uploadTrack: string;
  highlight: string;
  scrollbar: string;
  /** CSS filter for monochrome icons (audio, etc.) */
  iconFilter: string;
  /** CSS filter for black toolbar / action PNGs (emoji, send, reply, …) */
  toolbarIconFilter: string;
}

const lightTokens: IdigestChatThemeTokens = {
  bg: '#ffffff',
  fg: '#171717',
  muted: '#666666',
  border: '#e5e7eb',
  surface: '#f5f5f5',
  surfaceHover: '#eeeeee',
  buttonBorder: '#f5f5f5',
  buttonShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
  searchBorder: '#e0e0e0',
  searchIcon: '#666666',
  bubbleLeft: '#ffffff',
  bubbleRight: '#e0e0e0',
  bubbleBorder: '#aaaaaa',
  quoteBg: '#888888',
  quoteHover: '#999999',
  inputBg: '#ffffff',
  inputBorder: '#d0d0d0',
  voiceIdle: '#e0e0e0',
  voiceActive: '#ffffff',
  dialogBg: '#ffffff',
  dialogFg: '#333333',
  dialogButtonBg: 'rgb(242, 242, 242)',
  dialogButtonFg: '#000000',
  uploadBarBg: '#f3f2f1',
  uploadBarFg: '#605e5c',
  uploadTrack: '#c8c6c4',
  highlight: 'rgba(255, 215, 0, 0.25)',
  scrollbar: 'rgba(0, 0, 0, 0.3)',
  iconFilter: 'brightness(0)',
  toolbarIconFilter: 'none'
};

/** Aligned with vine-web dark neutrals. */
const darkTokens: IdigestChatThemeTokens = {
  bg: '#0a0a0a',
  fg: '#fafafa',
  muted: '#a3a3a3',
  border: '#262626',
  surface: '#171717',
  surfaceHover: '#262626',
  buttonBorder: '#262626',
  buttonShadow: '2px 2px 4px rgba(0, 0, 0, 0.45)',
  searchBorder: '#404040',
  searchIcon: '#a3a3a3',
  bubbleLeft: '#262626',
  bubbleRight: '#3f3f46',
  bubbleBorder: '#525252',
  quoteBg: '#404040',
  quoteHover: '#525252',
  inputBg: '#171717',
  inputBorder: '#404040',
  voiceIdle: '#262626',
  voiceActive: '#3f3f46',
  dialogBg: '#171717',
  dialogFg: '#e5e5e5',
  dialogButtonBg: '#262626',
  dialogButtonFg: '#fafafa',
  uploadBarBg: '#171717',
  uploadBarFg: '#a3a3a3',
  uploadTrack: '#404040',
  highlight: 'rgba(250, 204, 21, 0.2)',
  scrollbar: 'rgba(255, 255, 255, 0.25)',
  iconFilter: 'brightness(0) invert(1)',
  toolbarIconFilter: 'brightness(0) invert(1)'
};

export function getChatThemeTokens(theme: IdigestChatTheme = 'light'): IdigestChatThemeTokens {
  return theme === 'dark' ? darkTokens : lightTokens;
}

export function chatThemeCssVars(tokens: IdigestChatThemeTokens): Record<string, string> {
  return {
    '--idigest-chat-bg': tokens.bg,
    '--idigest-chat-fg': tokens.fg,
    '--idigest-chat-muted': tokens.muted,
    '--idigest-chat-border': tokens.border,
    '--idigest-chat-surface': tokens.surface,
    '--idigest-chat-surface-hover': tokens.surfaceHover,
    '--idigest-chat-bubble-left': tokens.bubbleLeft,
    '--idigest-chat-bubble-right': tokens.bubbleRight,
    '--idigest-chat-bubble-border': tokens.bubbleBorder,
    '--idigest-chat-quote-bg': tokens.quoteBg,
    '--idigest-chat-quote-hover': tokens.quoteHover,
    '--idigest-chat-input-bg': tokens.inputBg,
    '--idigest-chat-input-border': tokens.inputBorder,
    '--idigest-chat-search-border': tokens.searchBorder,
    '--idigest-chat-voice-idle': tokens.voiceIdle,
    '--idigest-chat-voice-active': tokens.voiceActive,
    '--idigest-chat-dialog-bg': tokens.dialogBg,
    '--idigest-chat-dialog-fg': tokens.dialogFg,
    '--idigest-chat-dialog-button-bg': tokens.dialogButtonBg,
    '--idigest-chat-dialog-button-fg': tokens.dialogButtonFg,
    '--idigest-chat-upload-bg': tokens.uploadBarBg,
    '--idigest-chat-upload-fg': tokens.uploadBarFg,
    '--idigest-chat-upload-track': tokens.uploadTrack,
    '--idigest-chat-highlight': tokens.highlight,
    '--idigest-chat-scrollbar': tokens.scrollbar,
    '--idigest-chat-icon-filter': tokens.iconFilter,
    '--idigest-chat-toolbar-icon-filter': tokens.toolbarIconFilter
  };
}
